import { type MutateOptions, useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useCallback } from 'react';
import { gqlClient, queryClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import {
  createOrganizationMembersQueryKey,
  isEventMessageRecipientsQueryKey,
} from '@/utils/queryKeys';
import type {
  UpdateOrganizationMemberMutation,
  UpdateOrganizationMemberMutationVariables,
} from './useUpdateOrganizationMember.generated';

const query = gql`
  mutation UpdateOrganizationMember($input: UpdateOrganizationMemberInput!) {
    updateOrganizationMember(input: $input)
  }
`;

// Owner-only: set a member's SMS contact details (display-name override +
// phone number). FAMILY orgs only — the server rejects other org types.
export const useUpdateOrganizationMember = (organizationId: string) => {
  const { mutate, ...rest } = useMutation<
    UpdateOrganizationMemberMutation,
    QueryError,
    UpdateOrganizationMemberMutationVariables
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        UpdateOrganizationMemberMutation,
        UpdateOrganizationMemberMutationVariables
      >(query, variables),
    // onSettled, not onSuccess: the server writes the membership row and then
    // fires the welcome text, so a rejection can arrive after the row has
    // already changed. Refetching only on success leaves that stale forever
    // (verified: an errored mutation refetches nothing at all), and refetching
    // after a genuine no-op failure costs one request.
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: createOrganizationMembersQueryKey(organizationId),
      });
      // A member's phone number is what makes them a text-message recipient, so
      // the event modal's picker is stale as soon as one is set or cleared —
      // and settings can be opened from that picker, so the two are on screen
      // together.
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => isEventMessageRecipientsQueryKey(queryKey),
      });
    },
  });

  const updateMember = useCallback(
    (
      userId: string,
      data: { name?: string | null; phoneNumber?: string | null },
      options?: MutateOptions<
        UpdateOrganizationMemberMutation,
        QueryError,
        UpdateOrganizationMemberMutationVariables
      >
    ) =>
      mutate(
        {
          input: {
            organizationId,
            userId,
            ...data,
          },
        },
        options
      ),
    [organizationId, mutate]
  );

  return { updateMember, ...rest };
};
